/**
 * Customer Loan Offers — Web mirror of the Mobile Loan flow.
 *
 * V4.24.45 — Aligned to Mobile app.
 * Uses /api/public/v1/loan-cases/* endpoints (same source of truth as Mobile)
 * so statuses, business rules, buttons, navigation, and validation are
 * identical across Web + Mobile customer/agent experiences.
 *
 * NOTE: Admin CMS and Banker Portal deliberately keep their own workflows —
 * this page is customer/agent-facing only.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Landmark, CheckCircle2, Clock, FileText, Loader2, ChevronRight,
  Search, X, ChevronLeft,
} from 'lucide-react';
import {
  LOAN_STATUS_META,
  OFFER_STATUS_META,
  routeForNextScreen,
  StatusBadge as SharedStatusBadge,
  OfferStatusBadge as SharedOfferStatusBadge,
} from './_shared';

// V4.24.46 — Re-export via local names so we don't have to rename usages below.
const StatusBadge = SharedStatusBadge;
const OfferStatusBadge = SharedOfferStatusBadge;

// V4.24.45 — Fallback next_screen mapping when the backend doesn't emit
// the hint (legacy records). Mirrors backend `_next_screen_for_case`.
function fallbackNextScreen(status, path) {
  if (['approved', 'rejected', 'drawdown', 'closed', 'spa_signed'].includes(status)) return 'loan_result';
  if (['draft', 'profiling', 'waiting_score'].includes(status)) return 'loan_questionnaire';
  if (['A_own_bank', 'D_any_bank'].includes(path)) return 'loan_detail';
  if (['waiting_bank', 'offer_accepted'].includes(status)) return 'loan_offers';
  if (['lo_meetup', 'lo_signed'].includes(status)) return 'loan_lo_meetup';
  if (status === 'ready_pick_banker') return 'loan_recommended_banks';
  if (status === 'bankers_available') return 'loan_available_bankers';
  if (path === 'B_outsource') return 'loan_detail';
  return 'loan_type_picker';
}

export default function CustomerLoanOffers() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [accepting, setAccepting] = useState(false);
  // V4.24.49 — Card tap opens inline "Update Progress" modal (spec-c).
  const [progressCase, setProgressCase] = useState(null);

  // V4.25.9 — Server-side filtering + pagination (backend already supports
  // ?status / ?search / ?page / ?limit on GET /public/v1/loan-cases).
  const LIMIT = 10;
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});

  // Granular statuses offered in the "More statuses" dropdown. Keys must
  // match the backend `_LOAN_STATUS_FILTERS` (single-status) keys.
  const GRANULAR_STATUSES = [
    'waiting_score', 'profiling', 'ready_pick_banker', 'waiting_bank',
    'waiting_outsource', 'bankers_available', 'waiting_banker', 'own_bank_waiting',
    'offer_accepted', 'lo_meetup', 'special_handling', 'lo_signed',
    'approved', 'drawdown', 'spa_signed', 'rejected',
  ];

  // V4.24.49 — 6-stage Type C progress + 4-step Type A/D progress for the
  // inline modal. Mirrors backend `_LOAN_STATUS_LABELS` progression.
  const TYPE_C_STAGES = [
    { key: 'waiting_score',     label: 'Waiting Score' },
    { key: 'ready_pick_banker', label: 'Ready Pick Bank' },
    { key: 'waiting_bank',      label: 'Waiting Bank' },
    { key: 'offer_accepted',    label: 'Offer Accepted' },
    { key: 'lo_meetup',         label: 'LO Meetup' },
    { key: 'lo_signed',         label: 'LO Signed' },
    { key: 'approved',          label: 'Approved' },
    { key: 'spa_signed',        label: 'SPA Signed' },
    { key: 'drawdown',          label: 'Drawn' },
  ];
  const TYPE_AD_STAGES = [
    { key: 'select_type',   label: 'Select Loan Type' },
    { key: 'upload_lo',     label: 'Upload Signed LO' },
    { key: 'decision',      label: 'Approve / Reject' },
    { key: 'spa_signed',    label: 'SPA Signed' },
  ];

  const buildProgress = (c) => {
    if (['A_own_bank', 'D_any_bank'].includes(c.selection_path)) {
      // 4-step: derived similarly to LoanDetail stepper
      const hasLo = (c.signed_lo_files || []).length > 0;
      const done = ['approved', 'rejected', 'drawdown', 'closed'].includes(c.status);
      const spa = ['drawdown', 'closed', 'spa_signed'].includes(c.status);
      return TYPE_AD_STAGES.map((s, i) => ({
        ...s,
        done: [true, hasLo, done, spa][i],
        current: i === TYPE_AD_STAGES.findIndex((_, ix) => ![true, hasLo, done, spa][ix]),
      }));
    }
    // Type C linear
    const currentIdx = TYPE_C_STAGES.findIndex(s => s.key === c.status);
    return TYPE_C_STAGES.map((s, i) => ({
      ...s,
      done: currentIdx > i,
      current: currentIdx === i,
    }));
  };

  // V4.24.45 — Load via the SAME `/api/public/v1/*` tree that Mobile uses.
  // Each list item already carries status + selection_path so we can render
  // the correct badge + next_screen hint without a second call.
  const loadCases = async () => {
    setLoading(true);
    try {
      const r = await api.get('/public/v1/loan-cases', {
        params: {
          status: statusFilter,
          search: search || undefined,
          page,
          limit: LIMIT,
        },
      });
      const items = r.data?.data?.items || r.data?.items || [];
      const pg = r.data?.data?.pagination || r.data?.pagination || {};
      setTotal(pg.total ?? items.length);
      setTotalPages(pg.total_pages ?? 1);
      setCounts(r.data?.data?.count || {});
      // For each case on THIS page (max 10), fetch full detail
      // (returns next_screen + offers). Server-side pagination keeps this
      // bounded so we never fan out to hundreds of detail calls.
      const detailed = await Promise.all(
        items.map(async (c) => {
          try {
            const d = await api.get(`/public/v1/loan-cases/${c.id}`);
            return { ...c, ...(d.data?.data || {}) };
          } catch { return c; }
        }),
      );
      setCases(detailed);
    } catch (e) {
      toast.error('Failed to load loan cases');
    } finally { setLoading(false); }
  };

  // Debounce the search box (400ms) → drives the server-side `search` param.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { loadCases(); /* eslint-disable-next-line */ }, [statusFilter, search, page]);

  const applyStatus = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  // V4.24.45 — Same endpoint the mobile app hits — no more `/customer/loans/*`.
  const accept = async () => {
    if (!confirmOffer) return;
    setAccepting(true);
    try {
      const r = await api.post(
        `/public/v1/loan-cases/${confirmOffer.case_id}/offers/${confirmOffer.offer.id}/accept`,
        { agreement_accepted: true },
      );
      const newStatus = r.data?.data?.new_status;
      const nextScreen = r.data?.data?.next_screen;
      toast.success(`Offer accepted — ${LOAN_STATUS_META[newStatus]?.label || 'updated'}`);
      setConfirmOffer(null);
      await loadCases();
      // Navigate per the backend's next_screen hint (if a downstream page exists).
      if (nextScreen === 'loan_lo_meetup') {
        navigate(`/loan/${confirmOffer.case_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Accept failed');
    } finally { setAccepting(false); }
  };

  // V4.24.49 — Per spec-c: card tap opens Update Progress modal inline.
  // Modal shows the timeline + a "Continue to next step" CTA that uses
  // the same next_screen contract for downstream navigation.
  const openCase = (c) => {
    setProgressCase(c);
  };

  const continueFromModal = () => {
    if (!progressCase) return;
    const target = progressCase.next_screen || fallbackNextScreen(progressCase.status, progressCase.selection_path);
    const route = routeForNextScreen(progressCase.id, target, progressCase.selection_path);
    setProgressCase(null);
    if (route !== '/loan-offers') navigate(route);
  };

  return (
    <div className="space-y-4 p-2 sm:p-4" data-testid="customer-loan-offers-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Landmark className="w-6 h-6 text-indigo-600" /> Loan Offers
        </h1>
        <p className="text-sm text-gray-500">
          Review and accept the best bank offer for each of your bookings. Same flow as the mobile app.
        </p>
      </div>

      {/* V4.25.9 — Filter bar: quick buckets + granular status + search */}
      <div className="bg-white border rounded-xl p-3 sm:p-4 space-y-3" data-testid="loan-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'confirm', label: 'Confirm' },
          ].map((chip) => {
            const active = statusFilter === chip.key;
            const n = counts?.[chip.key];
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => applyStatus(chip.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                  active
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                }`}
                data-testid={`loan-filter-chip-${chip.key}`}
              >
                {chip.label}
                {typeof n === 'number' && (
                  <span className={`ml-1.5 text-xs ${active ? 'text-indigo-100' : 'text-gray-400'}`}>{n}</span>
                )}
              </button>
            );
          })}

          <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />

          <Select
            value={GRANULAR_STATUSES.includes(statusFilter) ? statusFilter : undefined}
            onValueChange={applyStatus}
          >
            <SelectTrigger className="w-[190px] h-9 bg-gray-50 border-gray-200" data-testid="loan-status-dropdown">
              <SelectValue placeholder="More statuses…" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 max-h-72">
              {GRANULAR_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`loan-status-option-${s}`}>
                  {LOAN_STATUS_META[s]?.label || s.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by case ref or purchaser name…"
            className="pl-9 pr-9 bg-gray-50 border-gray-200"
            data-testid="loan-search-input"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="loan-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {loading && <p className="text-gray-500">Loading…</p>}

      {!loading && cases.length === 0 && (
        <div className="border border-dashed rounded-xl p-10 text-center text-gray-500 bg-white" data-testid="customer-loan-empty">
          <Clock className="w-10 h-10 mx-auto text-gray-300" />
          <p className="mt-2">
            {statusFilter !== 'all' || search
              ? 'No loan cases match your filters. Try clearing the search or status filter.'
              : 'No loan applications yet. They appear here automatically after you submit a booking.'}
          </p>
        </div>
      )}

      {cases.map((c) => {
        const offers = (c.offers || []).slice().sort((a, b) => (a.rate_percent || 999) - (b.rate_percent || 999));
        const acceptedId = c.accepted_offer_id;
        const selectedId = c.selected_offer_id;
        // V4.24.85 — Whole card is a link into the Loan Detail page.
        // The dedicated `Review & accept offer` CTA lives on Loan Detail
        // (image 3) so per-offer Accept buttons are no longer shown on
        // this list — a user cannot accidentally accept two offers from
        // two different loans on this page, and clicking anywhere on the
        // card takes them to the case they came to review.
        return (
          <div
            key={c.id}
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/loan/${c.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/loan/${c.id}`); }}
            className="bg-white border rounded-xl p-4 sm:p-5 space-y-3 cursor-pointer hover:shadow-md hover:border-indigo-200 transition"
            data-testid={`customer-loan-case-${c.id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-left">
                <p className="font-semibold text-gray-800 flex items-center gap-1">
                  {c.property_name || 'Property'}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </p>
                {(c.customer_name || c.buyer_name) && (
                  <p className="text-xs text-gray-600" data-testid={`loan-case-purchaser-${c.id}`}>
                    <span className="text-gray-500">Name Purchaser:</span>{' '}
                    <span className="font-medium">{c.customer_name || c.buyer_name}</span>
                  </p>
                )}
                <p className="text-xs text-gray-500 font-mono">{c.case_ref || c.case_code}</p>
              </div>
              <StatusBadge status={c.status} label={c.loan_status_label} />
            </div>

            {offers.length === 0 ? (
              <div className="rounded-lg p-3 bg-slate-50 text-sm text-slate-600">
                <Clock className="w-4 h-4 inline mr-1" />
                Waiting for banker offers. We&apos;ll notify you as soon as banks respond.
              </div>
            ) : (
              <div className="space-y-2">
                {offers.map((o) => {
                  const isAccepted = acceptedId === o.id || selectedId === o.id;
                  const isRejected = o.status === 'rejected';
                  return (
                    <div
                      key={o.id}
                      className={`border rounded-lg p-3 flex flex-wrap items-center gap-3 ${
                        isAccepted ? 'bg-emerald-50 border-emerald-300' : isRejected ? 'bg-slate-50 opacity-70' : 'bg-white'
                      }`}
                      data-testid={`customer-offer-${o.id}`}
                    >
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-semibold flex items-center gap-2">
                          {o.bank_code}
                          <OfferStatusBadge status={o.status} />
                        </div>
                        <span className="text-xs text-gray-500 block">{o.banker_name || ''}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Rate:</span>{' '}
                        <span className="font-bold text-lg text-indigo-700" data-testid={`offer-rate-${o.id}`}>
                          {o.rate_percent != null ? `${o.rate_percent}%` : '—%'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Amount:</span>{' '}
                        <span className="font-semibold">RM {Number(o.loan_amount || 0).toLocaleString('en-MY')}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Tenure:</span> {o.tenure_years || '—'} yrs
                      </div>
                      {o.lo_pdf_url && (
                        <a
                          href={o.lo_pdf_url.startsWith('http') ? o.lo_pdf_url : `${process.env.REACT_APP_BACKEND_URL}/api${o.lo_pdf_url}`}
                          target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-indigo-600 hover:underline text-xs flex items-center gap-1"
                          data-testid={`offer-lo-${o.id}`}
                        >
                          <FileText className="w-3 h-3" /> Letter of Offer
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* V4.25.9 — Pagination footer (server-side, 10 per page) */}
      {!loading && cases.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 pt-1" data-testid="loan-pagination">
          <p className="text-sm text-gray-500" data-testid="loan-pagination-info">
            {`Showing ${(page - 1) * LIMIT + 1}–${(page - 1) * LIMIT + cases.length} of ${total}`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="loan-page-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <span className="text-sm text-gray-600" data-testid="loan-page-indicator">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="loan-page-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* V4.24.49 — Inline Update Progress modal (image 4). Opens when a
          loan card is tapped. Shows the current status pill + progress
          timeline for Type A/D (4-step) or Type C (6-stage). */}
      <Dialog open={!!progressCase} onOpenChange={(o) => { if (!o) setProgressCase(null); }}>
        <DialogContent className="max-w-md" data-testid="progress-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Update Status
              {progressCase && <StatusBadge status={progressCase.status} label={progressCase.loan_status_label} />}
            </DialogTitle>
            <DialogDescription>
              {progressCase?.property_name || progressCase?.case_ref}
            </DialogDescription>
          </DialogHeader>
          {progressCase && (
            <div className="space-y-4">
              <div className="text-xs text-gray-500 space-y-0.5">
                <p><b>Case:</b> {progressCase.case_ref || progressCase.case_code}</p>
                {progressCase.selection_path && <p><b>Path:</b> {progressCase.selection_path}</p>}
              </div>
              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Your loan progress</p>
                <ul className="space-y-2" data-testid="progress-timeline">
                  {buildProgress(progressCase).map((s, i) => (
                    <li key={s.key} className="flex items-start gap-3">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                          s.done ? 'bg-emerald-600 text-white'
                            : s.current ? 'bg-indigo-600 text-white ring-2 ring-indigo-200'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                        data-testid={`progress-step-${s.key}`}
                      >
                        {s.done ? '✓' : i + 1}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm ${s.current ? 'font-semibold text-indigo-700' : s.done ? 'text-gray-700' : 'text-gray-500'}`}>
                          {s.label}
                          {s.current && <span className="ml-2 text-[10px] uppercase text-indigo-600">Current</span>}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressCase(null)} data-testid="progress-close-btn">Close</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={continueFromModal} data-testid="progress-continue-btn">
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmOffer} onOpenChange={(o) => { if (!o) setConfirmOffer(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept this offer?</DialogTitle>
            <DialogDescription>
              You&apos;re about to accept the <strong>{confirmOffer?.offer.bank_code}</strong> offer at{' '}
              <strong>{confirmOffer?.offer.rate_percent}%</strong> for{' '}
              <strong>RM {Number(confirmOffer?.offer.loan_amount || 0).toLocaleString('en-MY')}</strong> over{' '}
              <strong>{confirmOffer?.offer.tenure_years} years</strong>. All other offers on this case will be marked as
              superseded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOffer(null)} data-testid="offer-accept-cancel">Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={accepting}
              onClick={accept}
              data-testid="offer-accept-confirm"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
